import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';
import { humaniseIssueError } from '@/features/inventory/issue-lines/issueErrors';
import { usePatient } from '@/features/patients/usePatients';
import { VisitForm, type VisitFormValues } from '@/features/visits/VisitForm';
import { useAmendVisit, useVisit } from '@/features/visits/useVisits';
import { translateApiError } from '@/lib/apiError';

/** Correct a visit that has already been recorded. */
export function EditVisitPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { visitId = '' } = useParams();

  const visitQ = useVisit(visitId);
  const patientQ = usePatient(visitQ.data?.patientId);
  const amend = useAmendVisit(visitId);
  // Bumped on every successful save so the form remounts on the saved visit:
  // its local edits (changed quantities, lines marked for removal, newly picked
  // medicines) describe a difference that no longer exists.
  const [revision, setRevision] = useState(0);

  if (visitQ.isLoading || (visitQ.data && patientQ.isLoading)) {
    return <p className="py-10 text-center text-[13px] text-muted">{t('common.loading')}</p>;
  }
  if (visitQ.isError || patientQ.isError || !visitQ.data || !patientQ.data) {
    return (
      <div className="mx-auto max-w-md py-12 text-center">
        <p role="alert" className="text-[13.5px] text-destructive">
          {translateApiError(visitQ.error ?? patientQ.error, { fallback: t('visits.couldNotLoadVisit') })}
        </p>
        <Button className="mt-4" variant="secondary" onClick={() => navigate('/visits')}>
          {t('visits.backToVisits')}
        </Button>
      </div>
    );
  }

  const onSubmit = async (values: VisitFormValues) => {
    try {
      await amend.mutateAsync({
        ...(values.diagnosisNote ? { diagnosisNote: values.diagnosisNote } : {}),
        outsideMedicines: values.outsideMedicines,
        issued: { add: values.added, update: values.updated, remove: values.removed },
      });
      setRevision((n) => n + 1);
    } catch {
      // Captured into amend.error and rendered by the form.
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {revision > 0 && !amend.isPending && !amend.isError && (
        <div
          role="status"
          className="mx-auto flex w-full max-w-[920px] items-center gap-2 rounded-md border border-line bg-success-bg px-3 py-2.5 text-[13px] text-success"
        >
          <Icon name="check" size={15} className="shrink-0" />
          <span>{t('visits.changesSaved')}</span>
        </div>
      )}
      <VisitForm
        key={revision}
        mode={{ kind: 'edit', visit: visitQ.data }}
        patient={patientQ.data}
        saving={amend.isPending}
        error={amend.isError ? humaniseIssueError(amend.error, t) : null}
        onSubmit={onSubmit}
        onCancel={() => navigate('/visits')}
      />
    </div>
  );
}
